/**
 * Confirmation dialog for the helpdesk take-over / hand-back / claim actions.
 * Ported from the storybook `final/_confirm-dialog.tsx` (Attio-style Dialog with
 * a title, optional description, Cancel + confirm footer), minus the
 * storybook-only `FINAL_FONT` inline (the dashboard sets SF Pro at the body).
 *
 *   const { confirm, confirmDialog } = useConfirm();
 *   confirm({ title, description, confirmLabel, onConfirm });
 *   // render once: {confirmDialog}
 */
import * as React from "react";

import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@cometchat-ai/ui-dashboard";

export type ConfirmOptions = {
  title: string;
  description?: React.ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  /** Confirm-button styling. `default` = neutral, `danger` = destructive. */
  tone?: "danger" | "default";
  onConfirm: () => void;
};

export function useConfirm() {
  const [options, setOptions] = React.useState<ConfirmOptions | null>(null);
  const confirm = React.useCallback((opts: ConfirmOptions) => setOptions(opts), []);
  const confirmDialog = (
    <ConfirmDialog
      options={options}
      onOpenChange={(open) => (open ? undefined : setOptions(null))}
    />
  );
  return { confirm, confirmDialog };
}

function ConfirmDialog({
  options,
  onOpenChange,
}: {
  options: ConfirmOptions | null;
  onOpenChange: (open: boolean) => void;
}) {
  // Keep the last options through the close animation (open flips false first).
  const lastRef = React.useRef<ConfirmOptions | null>(null);
  if (options) lastRef.current = options;
  const shown = options ?? lastRef.current;

  return (
    <Dialog open={options !== null} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-sm gap-0 rounded-2xl p-0"
        onClick={(e) => e.stopPropagation()}
      >
        <DialogHeader className="space-y-1 px-5 pb-4 pt-5 text-left">
          <DialogTitle className="text-base font-semibold tracking-[0.01em]">
            {shown?.title}
          </DialogTitle>
          {shown?.description ? (
            <DialogDescription className="text-2sm text-muted-foreground">
              {shown.description}
            </DialogDescription>
          ) : null}
        </DialogHeader>
        <DialogFooter className="gap-2 px-5 pb-5">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-8 rounded-md text-2sm"
            onClick={() => onOpenChange(false)}
          >
            {shown?.cancelLabel ?? "Cancel"}
          </Button>
          <Button
            type="button"
            variant={shown?.tone === "danger" ? "destructive" : "default"}
            size="sm"
            className="h-8 rounded-md text-2sm"
            onClick={() => {
              shown?.onConfirm();
              onOpenChange(false);
            }}
          >
            {shown?.confirmLabel ?? "Confirm"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
